import React, { useState, useEffect } from 'react';
import { func, bool } from 'prop-types';
import Axios from 'axios';
import Button from '@hig/button';
import { Error24 } from '@hig/icons';
import Input from '@hig/input';
import Label from '@hig/label';
import ProgressRing from '@hig/progress-ring';
import AppTypeRadioBtns, { AppTypeArr } from './ForgeAppTypeRadioCards';

const api_client = Axios.create({
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
});

const screenWidth = window.innerWidth;

export default function CreateForgeApp({ close, clear }) {
    const closeModal = close;
    const [appName, setAppName] = useState('');
    const [appType, setAppType] = useState('');
    const [appNameErr, setAppNameErr] = useState('');
    const [appTypeErr, setAppTypeErr] = useState(false);
    const [showProgressRing, setShowProgressRing] = useState(false);

    // Highjacking the modal state to detect if it's reopening
    useEffect(() => {
        if (clear) {
            setAppName('');
            setAppNameErr('');
            setAppTypeErr(false);
            setAppType('');
        }
    }, [clear]);

    const onAppNameChangeHandler = (e) => {
        setAppName(e.target.value);
        if(appNameErr) {
            setAppNameErr('');
        }
    };

    const onAppTypeChangeHandler = (e) => {
        setAppType(e.target.value);
        if(appTypeErr){
            setAppTypeErr(false);
        }
    };

    const appNameLabelStyles = (styles) => {
        return {
            ...styles,
            label: {
                ...styles.label,
                fontFamily: "Artifakt Element, sans-serif",
                fontSize: "14px",
                lineHeight: "20px",
                color: '#3C3C3C',
                width: "200px",
                height: "20px"
            }
        };
    };

    const appNameInputStyles = (styles) => ({
        ...styles,
        wrapper: {
            ...styles.wrapper,
            marginRight: '8px',
            marginTop: "5px",
            width: (appNameErr && screenWidth > 690) ? "95%" : "100%",
        },
        input: {
            ...styles.input,
            "::selection": {},
            transitionDuration: "0.0s",
            transitionProperty: "none",
        },
        halo:{}
    });

    async function handleSubmit(event) {
        event.preventDefault();
        
        // Validation Check
        if (!appName || !appType) {
            if (!appName) setAppNameErr("An application name is required.");
            if (!appType) setAppTypeErr(true);
            return;
        }

        setShowProgressRing(true);

        try {
            const result = await api_client.post('/myapps/create-v2', {appName, appType});
            if (result.status === 201 && result.headers.location) {
                window.location = result.headers.location;
            }
        } catch (err) {
            setShowProgressRing(false);
            if (err.response && err.response.data && err.response.data.error) {
                setAppNameErr(err.response.data.message ? err.response.data.message : "An error occurred");
            }
            console.error("Error from create-v2: ", err);
        }

    }

    function cancelSubmit(event) {
        event.preventDefault();
        closeModal();
    }

    const cancelCustomStylesheet = (styles) => ({
        ...styles,
        button: {
            ...styles.button,
        }
    });

    return (
        <React.Fragment>
            <div className="adskf__forgeapp-create-body">
                <div className="adskf__forgeapp-create-body-content">
                    <form id="adskf__forgeapp-form" className="adskf__forgeapp-create-app-types-form">
                        <div className="adskf__forgeapp-create-app-types-name">
                            <Label stylesheet={appNameLabelStyles} htmlFor="appName">Name*</Label>
                            <div className="adskf__inputAppNameErrorState">
                                <Input stylesheet={appNameInputStyles} variant="box" id="appName" value={appName} error={!!appNameErr} onChange={onAppNameChangeHandler} />
                                {appNameErr ? <Error24 color="red" /> : null}
                            </div>
                            <div className="adskf_radioAppTypeErrorState">
                                {
                                    appNameErr
                                && <p style={{ color: "red", fontSize: "12px" }}>{appNameErr}</p>
                                }
                            </div>
                        </div>
                        <div className="adskf__forgeapp-create-app-types-container">
                            <div className="adskf__forgeapp-create-app-types-label">
                                <Label htmlFor="adsk__forgeapp-types">Application Type*</Label>
                            </div>
                            <div className="adskf__forgeapp-create-app-types" id="adskf__forgeapp-types">
                                {AppTypeArr.map((val, i) => <AppTypeRadioBtns key={i} appType={val} onClickMethod={onAppTypeChangeHandler} selectedAppType={appType}/> )}
                            </div>
                            <div className="adskf_radioAppTypeErrorState">
                                {
                                    appTypeErr
                                    && <p style={{ color: "red", fontSize: "12px" }}> You must select an application type.</p>

                                }
                            </div>
                            <div className="adskf__forgeapp-form-documentation-info-container">
                                <p className="adskf__forgeapp-form-documentation-info" style={{fontWeight: "400", color: "#3C3C3C"}}>
                                    If your project includes multiple applications, they should not share the same credentials.
                                    <br />
                                    For more information on choosing an app type, <a target="_blank" href="/en/docs/oauth/v2/developers_guide/App-types/">view documentation</a>.
                                </p>
                            </div>
                            <div className="adskf__forgeapp-create-app-actions">
                                <Button
                                    onClick={cancelSubmit}
                                    type="secondary"
                                    title="Cancel"
                                    stylesheet={cancelCustomStylesheet}
                                />
                                <Button
                                    onClick={handleSubmit}
                                    type="primary"
                                    title={showProgressRing ? "Creating app..." : "Create"}
                                    disabled={showProgressRing}
                                />
                                {showProgressRing ? <ProgressRing size="s" /> : null}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </React.Fragment>
    );
}

CreateForgeApp.propTypes = {
    close: func,
    clear: bool
};
